import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const ROLE_LABELS = {
  traffic: 'תנועה אורגנית',
  leads_total: 'סה"כ פניות',
  forms: 'פניות - טפסים',
  calls: 'פניות - שיחות',
  whatsapp: 'פניות - וואטסאפ',
  chat: "פניות - צ'אט",
  email: 'פניות - מייל',
  other: 'פניות - ערוץ אחר',
  extra: 'מדד נוסף (בשמו)',
  ignore: 'לא להציג',
};

const MISSING_LABELS = {
  traffic: 'תנועה אורגנית לחודש זה (יש להזין, או לשייך מדד בטבלה למטה)',
  'leads.total': 'פניות או מדד מכירות לחודש זה',
  client_name: 'שם לקוח',
  report_month: 'חודש',
};

const RANK_LABELS = { up: 'עלייה', stable: 'שמירה', mixed: 'מעורב', down: 'חיזוק (ירידה)' };

const RANK_OPTIONS = [
  { value: 'auto', label: 'אוטומטי לפי המיקומים הנוכחיים' },
  { value: '', label: 'ללא שורת מיקומים' },
  { value: 'up', label: 'עלייה' },
  { value: 'stable', label: 'שמירה' },
  { value: 'mixed', label: 'מעורב' },
  { value: 'down', label: 'חיזוק (ירידה)' },
];

const DIRECTION = {
  up: { text: 'עלייה', color: 'var(--green)' },
  down: { text: 'ירידה', color: 'var(--red)' },
  stable: { text: 'יציב', color: 'var(--text-muted)' },
  na: { text: 'אין השוואה', color: 'var(--text-muted)' },
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: '8px',
  border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)',
  fontSize: '13px', fontFamily: 'inherit',
};
const labelStyle = { fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' };

function lines(text) {
  return (text || '').split('\n').map((l) => l.trim()).filter(Boolean);
}

// "טקסט המשימה | 2" -> {text, hours: 2}. Hours are optional.
function parseTasks(text) {
  return lines(text).map((l) => {
    const parts = l.split('|');
    const hours = parts.length > 1 ? parseFloat(parts[parts.length - 1]) : NaN;
    if (parts.length > 1 && !isNaN(hours)) {
      return { text: parts.slice(0, -1).join('|').trim(), hours: hours, details: [] };
    }
    return { text: l, hours: null, details: [] };
  });
}

function formatTasks(tasks) {
  return (tasks || []).map((t) => t.text + (t.hours != null ? ' | ' + t.hours : '')).join('\n');
}

function fmt(v) {
  return v == null ? '-' : Number(v).toLocaleString('en-US');
}

function directionOf(computed, metric) {
  if (!computed || metric.current == null) return null;
  const pool = [computed.traffic, computed.leads_total]
    .concat(computed.leads_breakdown || [], computed.extra_metrics || [])
    .filter(Boolean);
  if (metric.role === 'traffic') return computed.traffic;
  if (metric.role === 'leads_total') return computed.leads_total;
  return pool.find((p) => p.label === metric.label && p.current === metric.current) || null;
}

// Step in the report flow: analyses the month's business results against the
// previous month, asks about planned articles / open items, and produces the
// client email text (editable) plus an internal note for the SEO manager.
export default function MonthlySummaryStep({ siteId, month, step, refreshKey, locked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  const [articles, setArticles] = useState('');
  const [openItems, setOpenItems] = useState('');
  const [contactName, setContactName] = useState('');
  const [rankDir, setRankDir] = useState('auto');
  const [manualTasks, setManualTasks] = useState('');
  const [picked, setPicked] = useState([]);
  const [managerNotes, setManagerNotes] = useState('');
  const [config, setConfig] = useState({});
  const [report, setReport] = useState('');

  // The form is seeded once per site+month; later reloads (after the monthly
  // numbers are saved, or a metric is re-mapped) must not wipe what was typed.
  const seededFor = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getMonthlySummary(siteId, month)
      .then((d) => {
        setData(d);
        setConfig(d.config || {});
        const key = siteId + '/' + month;
        if (seededFor.current !== key) {
          seededFor.current = key;
          const inp = (d.saved && d.saved.inputs) || null;
          setArticles(inp ? (inp.articles || []).join('\n') : '');
          setOpenItems(inp ? (inp.open_items || []).join('\n') : '');
          setContactName(d.contact_name || '');
          // Older saved summaries have no `mode` — fall back to the direction they stored.
          const rk = (inp && inp.rankings) || null;
          setRankDir(!inp ? 'auto' : (rk && rk.mode != null ? rk.mode : ((rk && rk.direction) || '')));
          setManualTasks(inp ? formatTasks(inp.manual_tasks) : '');
          setManagerNotes(inp && inp.manager_notes ? inp.manager_notes : '');
          setPicked(inp
            ? (inp.catalog_keys || [])
            : (d.suggested_tasks || []).filter((t) => t.recommended).map((t) => t.key));
          setReport(d.saved ? d.saved.client_report : '');
          setMsg(null);
        }
      })
      .catch((err) => setMsg({ type: 'error', text: 'שגיאה בטעינת הסיכום: ' + err.message }))
      .finally(() => setLoading(false));
  }, [siteId, month]);

  useEffect(() => { load(); }, [load, refreshKey]);

  function togglePick(key) {
    setPicked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev.concat([key])));
  }

  function changeRole(metricId, role) {
    // Only store real overrides, so improved auto-detection keeps applying to the rest.
    const map = {};
    (data.metrics || []).forEach((m) => {
      const r = m.id === metricId ? role : m.role;
      if (r !== m.auto_role) map[m.id] = r;
    });
    api.saveMonthlySummaryConfig(siteId, { metric_map: map })
      .then(load)
      .catch((err) => setMsg({ type: 'error', text: 'שגיאה בשמירת השיוך: ' + err.message }));
  }

  function handleGenerate() {
    if (report && !window.confirm('הפקה מחדש תחליף את הטקסט הקיים (כולל עריכות ידניות). להמשיך?')) return;
    setBusy(true);
    setMsg(null);
    api.generateMonthlySummary(siteId, month, {
      articles: lines(articles),
      open_items: lines(openItems),
      contact_name: contactName.trim() || null,
      rankings: { direction: rankDir || null },
      tasks: parseTasks(manualTasks),
      catalog_keys: picked,
      manager_notes: managerNotes.trim() || null,
      config: config,
    })
      .then((saved) => {
        setData((prev) => ({ ...prev, saved: saved }));
        setReport(saved.client_report);
        setPicked((saved.inputs && saved.inputs.catalog_keys) || picked);
        setMsg({ type: 'success', text: 'הסיכום הופק. יש לעבור על הטקסט ועל ההערה הפנימית לפני השליחה.' });
      })
      .catch((err) => setMsg({ type: 'error', text: 'שגיאה: ' + err.message }))
      .finally(() => setBusy(false));
  }

  function handleSave() {
    setBusy(true);
    api.saveMonthlySummary(siteId, month, report)
      .then((saved) => {
        setData((prev) => ({ ...prev, saved: saved }));
        setMsg({ type: 'success', text: 'הטקסט נשמר וימלא את גוף המייל בשליחה.' });
      })
      .catch((err) => setMsg({ type: 'error', text: 'שגיאה בשמירה: ' + err.message }))
      .finally(() => setBusy(false));
  }

  function handleCopy() {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(report).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const saved = data && data.saved;
  const dirty = !!saved && report !== saved.client_report;
  const missing = (data && data.missing) || [];
  const hint = data && data.rankings_hint;
  const pickedCount = picked.length + lines(manualTasks).length + (lines(articles).length ? 1 : 0);

  return (
    <div className="section-card" style={{ padding: '20px', marginTop: '16px' }}>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {step != null && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, background: 'var(--accent)', color: '#fff', fontSize: '13px', fontWeight: 700 }}>{step}</span>
          )}
          <div style={{ fontWeight: 700, fontSize: '16px' }}>סיכום ותוכנית לחודש הקרוב</div>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', paddingRight: step != null ? '34px' : 0 }}>
ניתוח התוצאות העסקיות מול החודש הקודם והפקת טקסט המייל ללקוח. המספרים מחושבים במערכת; הניסוח ניתן לעריכה.
          {data && data.site_type ? ' סוג אתר: ' + (data.site_type === 'ecommerce' ? 'חנות אונליין' : 'לידים') + '.' : ''}
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '8px 12px', borderRadius: '8px', margin: '10px 0', fontSize: '13px',
          background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          color: msg.type === 'success' ? 'var(--green)' : 'var(--red)',
        }}>{msg.text}</div>
      )}

      {locked && (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>יש להזין ולשמור נתונים חודשיים (שלב 2) תחילה.</div>
      )}
      {!locked && loading && !data && (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>טוען...</div>
      )}

      {!locked && data && (
        <React.Fragment>
          {/* What was computed */}
          {(data.metrics || []).length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: '14px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['מדד', 'החודש', 'חודש קודם', 'שינוי', 'תפקיד בסיכום'].map((h) => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'right', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.metrics.map((m) => {
                    const st = m.role === 'ignore' ? null : directionOf(data.computed, m);
                    const d = st ? DIRECTION[st.direction] : null;
                    return (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border)', opacity: m.role === 'ignore' ? 0.55 : 1 }}>
                        <td style={{ padding: '6px 10px' }}>{m.group_label} - {m.label}</td>
                        <td style={{ padding: '6px 10px', direction: 'ltr', textAlign: 'right' }}>{fmt(m.current)}</td>
                        <td style={{ padding: '6px 10px', direction: 'ltr', textAlign: 'right' }}>{fmt(m.previous)}</td>
                        <td style={{ padding: '6px 10px', color: d ? d.color : 'var(--text-muted)' }}>
                          {d ? d.text + (st.pct != null ? ' (' + (st.pct > 0 ? '+' : '') + Math.round(st.pct) + '%)' : '') : '-'}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: '12px' }}>
                            {(data.roles || []).map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {missing.length > 0 && (
            <div style={{ padding: '10px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', background: 'rgba(239,68,68,0.1)', color: 'var(--red)' }}>
              אי אפשר להפיק סיכום - חסר: {missing.map((k) => MISSING_LABELS[k] || k).join('; ')}
            </div>
          )}

          {data.computed && (data.computed.flags || []).length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px', direction: 'ltr', textAlign: 'right' }}>
              {data.computed.flags.join(' · ')}
            </div>
          )}

          {/* Questions asked before generating */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
            <div>
              <label style={labelStyle}>מאמרים מתוכננים לחודש הקרוב (שם מאמר בכל שורה)</label>
              <textarea value={articles} onChange={(e) => setArticles(e.target.value)} rows={3} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>דברים פתוחים מול הלקוח (פריט בכל שורה)</label>
              <textarea value={openItems} onChange={(e) => setOpenItems(e.target.value)} rows={3} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>שם איש קשר לפנייה (אופציונלי)</label>
              <input value={contactName} onChange={(e) => setContactName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>שורת מיקומים</label>
              <select value={rankDir} onChange={(e) => setRankDir(e.target.value)} style={inputStyle}>
                {RANK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {hint && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  {hint.improved} ביטויים עלו, {hint.declined} ירדו, {hint.unchanged} ללא שינוי, {hint.top10} ב-Top 10
                  {hint.direction ? ' - אוטומטי: ' + RANK_LABELS[hint.direction] : ''} (קריאות "לא נמצא" לא נספרות).
                </div>
              )}
            </div>
          </div>

          {/* Work plan */}
          <div style={{ marginTop: '16px' }}>
            <label style={labelStyle}>משימות מוצעות לפי תוצאות החודש (מומלץ 3-5 משימות בסך הכל; נבחרו {pickedCount})</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '6px' }}>
              {(data.suggested_tasks || []).map((t) => (
                <label key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={picked.includes(t.key)} onChange={() => togglePick(t.key)} style={{ marginTop: '3px', flexShrink: 0 }} />
                  <span>
                    {t.text}
                    {t.used_last_month && <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}> (היה בחודש שעבר)</span>}
                  </span>
                </label>
              ))}
            </div>
            <label style={{ ...labelStyle, marginTop: '12px' }}>משימות נוספות (משימה בכל שורה; שעות אופציונלי אחרי |, למשל: שדרוג עמוד הבית | 2)</label>
            <textarea value={manualTasks} onChange={(e) => setManualTasks(e.target.value)} rows={3} style={inputStyle} />
            <label style={{ ...labelStyle, marginTop: '12px' }}>הערות למודל (אופציונלי, לא נשלח ללקוח)</label>
            <textarea value={managerNotes} onChange={(e) => setManagerNotes(e.target.value)} rows={2} style={inputStyle} />
          </div>

          {/* Display settings (remembered per site) */}
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '14px', fontSize: '13px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.task_mode === 'condense_3'} onChange={(e) => setConfig({ ...config, task_mode: e.target.checked ? 'condense_3' : 'all' })} />
              לדחוס ל-3 משימות
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!config.show_percentages} onChange={(e) => setConfig({ ...config, show_percentages: e.target.checked })} />
              אחוזי שינוי בעליות
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} title="בירידה: להציג גם את המספר של החודש הקודם (בלי המילה ירידה). כבוי = רק המספר הנוכחי">
              <input type="checkbox" checked={!!config.compare_declines} onChange={(e) => setConfig({ ...config, compare_declines: e.target.checked })} />
              השוואה גם בירידות
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!config.show_hours} onChange={(e) => setConfig({ ...config, show_hours: e.target.checked })} />
              להציג שעות
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} title="מוסיף לקטלוג משימות פרופיל עסקי וביקורות בגוגל (מההפקה הבאה)">
              <input type="checkbox" checked={!!config.is_local} onChange={(e) => setConfig({ ...config, is_local: e.target.checked })} />
              עסק מקומי
            </label>
          </div>

          <div style={{ marginTop: '14px' }}>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={busy || missing.length > 0}
              style={{ padding: '8px 16px', opacity: (busy || missing.length > 0) ? 0.5 : 1 }}>
              {busy ? 'מעבד...' : (saved ? 'הפק מחדש' : 'הפק סיכום')}
            </button>
          </div>

          {/* Result */}
          {saved && (
            <div style={{ marginTop: '18px' }}>
              {!saved.is_ready && (
                <div style={{ padding: '10px 12px', borderRadius: '8px', marginBottom: '10px', fontSize: '13px', fontWeight: 700, background: 'rgba(239,68,68,0.12)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  הטקסט לא עבר את הבדיקות האוטומטיות - לא מוכן לשליחה. יש לתקן ידנית ולשמור, או להפיק מחדש.
                </div>
              )}
              {(saved.warnings || []).length > 0 && (
                <ul style={{ margin: '0 0 10px', paddingRight: '18px', fontSize: '12px', color: 'var(--orange)' }}>
                  {saved.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              <label style={labelStyle}>טקסט המייל ללקוח (ניתן לעריכה)</label>
              <textarea value={report} onChange={(e) => setReport(e.target.value)} rows={16} style={{ ...inputStyle, lineHeight: 1.7, fontSize: '14px' }} />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={busy || !dirty} style={{ padding: '6px 14px', fontSize: '13px', opacity: (busy || !dirty) ? 0.5 : 1 }}>שמור עריכה</button>
                <button className="btn btn-secondary" onClick={handleCopy} style={{ padding: '6px 14px', fontSize: '13px' }}>{copied ? '✓ הועתק' : 'העתק טקסט'}</button>
                {dirty && <span style={{ fontSize: '12px', color: 'var(--orange)' }}>יש עריכות שלא נשמרו - המייל ימולא מהגרסה השמורה.</span>}
              </div>

              {saved.internal_note && (
                <div style={{ marginTop: '14px', padding: '12px 14px', borderRadius: '8px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: 'var(--orange)', marginBottom: '6px' }}>הערה פנימית - לא נשלח ללקוח</div>
                  <div style={{ fontSize: '13px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{saved.internal_note}</div>
                </div>
              )}
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
}
